import React, { FC, useState, useMemo, useCallback } from "react";
import BottomSheet from "shared/BottomSheet/BottomSheet";
import NcImage from "shared/NcImage/NcImage";
import { Product } from "data/data";
import { useCartDataProvider } from "context/CartProductContext";
import { validateModifiers, getModifierPrice } from "utils/modifierHelpers";
import { getChildModifierSets } from "services/HomeService";
import ModifierSection from "./ModifierSection";
import NestedModifierSection, {
  ChildBundle,
  ChildModItem,
  ChildModSet,
} from "./NestedModifierSection";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ShoppingBagIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

export interface ProductCustomizationSheetProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  editMode?: {
    cartItemId: number;
    modifierChecked: Record<number, Record<number, boolean>>;
    quantity: number;
    note: string;
  };
}

const EMPTY_BUNDLE: ChildBundle = {
  fetched: null,
  loading: false,
  selections: {},
  children: {},
};

/** Read the bundle at `path` (array of modifier-item-ids). Returns undefined if the path doesn't exist. */
function getBundleAt(
  root: Record<number, ChildBundle>,
  path: number[]
): ChildBundle | undefined {
  if (path.length === 0) return undefined;
  let cur: ChildBundle | undefined = root[path[0]];
  for (let i = 1; i < path.length && cur; i++) {
    cur = cur.children[path[i]];
  }
  return cur;
}

/** Immutably update the bundle at `path`, creating empty bundles along the way if missing. */
function updateBundleAt(
  root: Record<number, ChildBundle>,
  path: number[],
  updater: (b: ChildBundle) => ChildBundle
): Record<number, ChildBundle> {
  if (path.length === 0) return root;
  const [head, ...tail] = path;
  const existing = root[head] ?? EMPTY_BUNDLE;
  if (tail.length === 0) {
    return { ...root, [head]: updater(existing) };
  }
  const newChildren = updateBundleAt(existing.children, tail, updater);
  return { ...root, [head]: { ...existing, children: newChildren } };
}

/** Remove the bundle at `path` (used when a parent modifier item is unchecked). */
function removeBundleAt(
  root: Record<number, ChildBundle>,
  path: number[]
): Record<number, ChildBundle> {
  if (path.length === 0) return root;
  const [head, ...tail] = path;
  if (!(head in root)) return root;
  if (tail.length === 0) {
    const next = { ...root };
    delete next[head];
    return next;
  }
  const existing = root[head];
  if (!existing) return root;
  const newChildren = removeBundleAt(existing.children, tail);
  return { ...root, [head]: { ...existing, children: newChildren } };
}

/** Recursively sum onlinePrice of every selected item in a bundle tree. */
function sumBundlePrice(bundle: ChildBundle | undefined): number {
  if (!bundle || !bundle.fetched) return 0;
  let total = 0;
  for (const set of bundle.fetched) {
    const sel = bundle.selections[set.id] ?? {};
    for (const item of set.items) {
      if (sel[item.id]) total += item.onlinePrice || 0;
    }
  }
  for (const childBundle of Object.values(bundle.children)) {
    total += sumBundlePrice(childBundle);
  }
  return total;
}

/** Recursively check every required set in a bundle tree; returns true if valid. */
function validateBundle(bundle: ChildBundle | undefined): boolean {
  if (!bundle || !bundle.fetched) return true;
  for (const set of bundle.fetched) {
    const sel = bundle.selections[set.id] ?? {};
    const selectedCount = Object.values(sel).filter(Boolean).length;
    if (set.noOfRequired > 0 && selectedCount < set.noOfRequired) return false;
    if (set.maximum > 0 && selectedCount > set.maximum) return false;
  }
  for (const childBundle of Object.values(bundle.children)) {
    if (!validateBundle(childBundle)) return false;
  }
  return true;
}

const ProductCustomizationSheet: FC<ProductCustomizationSheetProps> = ({
  product,
  isOpen,
  onClose,
  editMode,
}) => {
  const { dispatch } = useCartDataProvider();

  const [modifierState, setModifierState] = useState<
    Record<number, Record<number, boolean>>
  >(editMode?.modifierChecked || {});
  const [nestedState, setNestedState] = useState<Record<number, ChildBundle>>(
    {}
  );
  const [quantity, setQuantity] = useState(editMode?.quantity || 1);
  const [note, setNote] = useState(editMode?.note || "");
  const [showError, setShowError] = useState(false);

  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setModifierState(editMode?.modifierChecked || {});
      setNestedState({});
      setQuantity(editMode?.quantity || 1);
      setNote(editMode?.note || "");
      setShowError(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, product, editMode]);

  /**
   * Fetch child modifier sets for a modifier item, storing them at `path` in nestedState.
   * `path` is the chain of parent modifier-item-ids leading to and including the fetched item.
   */
  const fetchAndStoreChildren = useCallback(
    async (path: number[], modifierItemId: number) => {
      setNestedState((prev) =>
        updateBundleAt(prev, path, (b) => ({ ...b, loading: true, error: undefined }))
      );
      try {
        const res = await getChildModifierSets<ChildModSet[]>(modifierItemId);
        const sets = (Array.isArray(res?.data) ? res.data : []) as ChildModSet[];
        setNestedState((prev) =>
          updateBundleAt(prev, path, (b) => ({
            ...b,
            fetched: sets,
            loading: false,
            error: undefined,
          }))
        );
      } catch (err: any) {
        setNestedState((prev) =>
          updateBundleAt(prev, path, (b) => ({
            ...b,
            loading: false,
            error: String(err?.message ?? err),
          }))
        );
      }
    },
    []
  );

  /** Top-level modifier item toggled from ModifierSection. */
  const handleTopLevelChange = useCallback(
    (modifierSetId: number, linkItemId: number, checked: boolean) => {
      // Update the top-level selection state (keyed by link id, unchanged behavior)
      setModifierState((prev) => {
        const updated = { ...prev };
        if (!updated[modifierSetId]) updated[modifierSetId] = {};
        if (checked) {
          updated[modifierSetId] = { ...updated[modifierSetId], [linkItemId]: true };
        } else {
          const setCopy = { ...updated[modifierSetId] };
          delete setCopy[linkItemId];
          updated[modifierSetId] = setCopy;
        }
        return updated;
      });
      setShowError(false);

      if (!product) return;
      const setObj = product.modifierSets?.find((s: any) => s.id === modifierSetId);
      const link = setObj?.modifierSetModifierItems?.find(
        (l: any) => l.id === linkItemId
      );
      const modItem: any = link?.modifierItem;
      if (!modItem) return;
      const modItemId: number = modItem.id;

      if (checked) {
        if (modItem.hasChildModifierSets) {
          fetchAndStoreChildren([modItemId], modItemId);
        }
      } else {
        // Uncheck → discard this item's subtree
        setNestedState((prev) => removeBundleAt(prev, [modItemId]));
      }
    },
    [product, fetchAndStoreChildren]
  );

  /** Nested child item toggled from NestedModifierSection at a given path. */
  const handleNestedToggle = useCallback(
    (path: number[], set: ChildModSet, item: ChildModItem, checked: boolean) => {
      setShowError(false);
      const isSingleSelect = set.maximum === 1;

      setNestedState((prev) => {
        return updateBundleAt(prev, path, (b) => {
          const newSelections = { ...b.selections };
          const prevSetSelections = newSelections[set.id] ?? {};
          let nextSetSelections: Record<number, boolean>;
          if (checked) {
            nextSetSelections = isSingleSelect
              ? { [item.id]: true }
              : { ...prevSetSelections, [item.id]: true };
          } else {
            nextSetSelections = { ...prevSetSelections };
            delete nextSetSelections[item.id];
          }
          newSelections[set.id] = nextSetSelections;

          // On radio-swap in a single-select set, discard any subtrees for items we're
          // deselecting; on uncheck, discard this item's own subtree.
          const previouslySelectedIds = Object.keys(prevSetSelections)
            .filter((k) => prevSetSelections[Number(k)])
            .map(Number);
          const nextSelectedIds = Object.keys(nextSetSelections)
            .filter((k) => nextSetSelections[Number(k)])
            .map(Number);
          const deselected = previouslySelectedIds.filter(
            (id) => !nextSelectedIds.includes(id)
          );
          let newChildren = b.children;
          for (const id of deselected) {
            if (id in newChildren) {
              const next = { ...newChildren };
              delete next[id];
              newChildren = next;
            }
          }
          return { ...b, selections: newSelections, children: newChildren };
        });
      });

      if (checked && item.hasChildModifierSets) {
        fetchAndStoreChildren([...path, item.id], item.id);
      }
    },
    [fetchAndStoreChildren]
  );

  /** Recursive renderer: returns the JSX for a bundle's fetched sets, or a loading/error/nothing indicator. */
  const renderBundle = useCallback(
    (path: number[], depth: number): React.ReactNode => {
      const bundle = getBundleAt(nestedState, path);
      if (!bundle) return null;
      if (bundle.loading) {
        return (
          <div className="ml-6 py-2 text-xs text-slate-500 dark:text-slate-400">
            Loading options…
          </div>
        );
      }
      if (bundle.error) {
        return (
          <div className="ml-6 py-2 text-xs text-red-500 dark:text-red-400">
            {bundle.error}
          </div>
        );
      }
      if (!bundle.fetched || bundle.fetched.length === 0) return null;
      const sorted = [...bundle.fetched].sort(
        (a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0)
      );
      return (
        <div className="pl-4">
          {sorted.map((set) => (
            <NestedModifierSection
              key={set.id}
              set={set}
              selectedInSet={bundle.selections[set.id] ?? {}}
              onToggle={(item, checked) =>
                handleNestedToggle(path, set, item, checked)
              }
              renderChildrenAfter={(itemId) => renderBundle([...path, itemId], depth + 1)}
              showError={showError}
              depth={depth}
            />
          ))}
        </div>
      );
    },
    [nestedState, handleNestedToggle, showError]
  );

  // Running total = base + top-level mods + all nested selections, times qty.
  const runningTotal = useMemo(() => {
    if (!product) return 0;
    const basePrice = product.onlinePrice;
    const topModPrice = getModifierPrice(product, modifierState);
    const nestedPrice = Object.values(nestedState).reduce(
      (acc, b) => acc + sumBundlePrice(b),
      0
    );
    return (basePrice + topModPrice + nestedPrice) * quantity;
  }, [product, modifierState, nestedState, quantity]);

  // Add-to-Cart is disabled if any top-level required set or any nested required set is unsatisfied.
  const isValid = useMemo(() => {
    if (!product) return false;
    const top = validateModifiers(product, modifierState);
    if (!top.valid) return false;
    for (const b of Object.values(nestedState)) {
      if (!validateBundle(b)) return false;
    }
    return true;
  }, [product, modifierState, nestedState]);

  const handleAddToCart = () => {
    if (!product) return;

    if (!isValid) {
      setShowError(true);
      const top = validateModifiers(product, modifierState);
      const firstErrorId = top.errors[0]?.modifierSetId;
      if (firstErrorId) {
        const el = document.querySelector(`[data-modifier-set-id="${firstErrorId}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Nested selection tree — logged for backend payload design. Attached to the cart
    // product under `nestedModifierSelections` so downstream consumers can inspect it.
    console.log("[NestedModifiers] final selection tree", nestedState);

    const cartProduct: any = {
      ...product,
      quantity,
      modifierChecked: modifierState,
      nestedModifierSelections: nestedState,
      note,
    };

    if (editMode) {
      dispatch({
        type: "EDIT_CART",
        payload: { ...cartProduct, cartItemId: editMode.cartItemId },
      });
    } else {
      dispatch({ type: "ADD_TO_CART", payload: cartProduct });
    }

    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700">
              {product.imageUrl && (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{product.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {editMode ? "Updated in cart" : "Added to cart"}
              </p>
            </div>
          </div>
        </Transition>
      ),
      { position: "bottom-center", id: `customize-${product.id}`, duration: 1500 }
    );

    onClose();
  };

  if (!product) return null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      {/* Product image */}
      <div className="relative w-full h-48 sm:h-56 bg-stone-100 dark:bg-slate-800">
        <NcImage
          containerClassName="w-full h-full"
          src={product.imageUrl || ""}
          className="object-cover w-full h-full"
        />
      </div>

      {/* Product info */}
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {product.name}
        </h2>
        <p className="text-base font-bold text-primary-600 dark:text-primary-400 mt-1">
          ${product.onlinePrice.toFixed(2)}
          {product.unit?.name && (
            <span className="text-xs font-normal text-slate-400 ml-1">
              / {product.unit.name}
            </span>
          )}
        </p>
        {product.description && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            {product.description}
          </p>
        )}
      </div>

      {/* Modifier sections (with inline nested expansion) */}
      {product.modifierSets && product.modifierSets.length > 0 && (
        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {product.modifierSets.map((modSet) => (
            <ModifierSection
              key={modSet.id}
              modifierSet={modSet}
              selectedState={modifierState[modSet.id] || {}}
              onChange={handleTopLevelChange}
              showError={showError}
              renderNestedAfter={(modifierItemId) => renderBundle([modifierItemId], 1)}
            />
          ))}
        </div>
      )}

      {/* Note section */}
      <div className="px-4 py-3">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Special Instructions
        </h4>
        <textarea
          placeholder="Any special requests?"
          className="w-full h-16 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {/* Sticky footer: quantity + add to cart */}
      <div className="sticky bottom-0 px-4 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
        <div className="flex items-center gap-3">
          {/* Quantity control */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full px-1 py-1">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
              disabled={quantity <= 1}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <span className="w-8 text-center font-bold text-slate-900 dark:text-slate-100">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Add to cart button with running total (disabled if required sets unsatisfied) */}
          <button
            onClick={handleAddToCart}
            disabled={!isValid}
            className={`flex-1 h-12 flex items-center justify-center gap-2 rounded-xl font-bold text-base transition-colors shadow-md active:scale-[0.98] ${
              isValid
                ? "bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 hover:bg-accent-600 dark:hover:bg-accent-500"
                : "bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
            }`}
          >
            <ShoppingBagIcon className="w-5 h-5" />
            <span>
              {editMode ? "Update" : "Add to Cart"} - ${runningTotal.toFixed(2)}
            </span>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ProductCustomizationSheet;
